import React, { FC } from "react";

import { type Resale, ResaleStatuses, SortDirection, useListGroupedResalesQuery } from "@api/graphql";
import { GroupedResales } from "@components/grouped_resales/GroupedResales";
import { ResalesAccordionHeader } from "@components/grouped_resales/ResaleAccordionHeader";
import { ProtectedComponent, useIsAuthUserAllowed } from "@components/permission_control/ProtectedComponent";
import type { TableFilterInput } from "@components/table/TableFilter";
import { useAuthenticationContext } from "@context/authentication";
import { ExternalRoles, InternalRoles, Role } from "@roles";
import type { SortByTyped } from "@util/index";
import type { ResaleGroup } from "@util/resale";

const resaleStatusFilterOptions: TableFilterInput[] = [
  {
    option: ResaleStatuses.PROVISIONALLY_ELIGIBLE,
    defaultValue: true,
  },
  {
    option: ResaleStatuses.PROVISIONALLY_INELIGIBLE,
    defaultValue: false,
  },
];

export const UnderReviewResales: FC<{ displayBanner?: boolean; vendorId?: number }> = ({ displayBanner, vendorId }) => {
  const { auth } = useAuthenticationContext();
  const sort_by: SortByTyped<Resale> = {
    field: "reported_on",
    direction: SortDirection.DESCENDING,
  };
  const isExternalUser = useIsAuthUserAllowed(ExternalRoles);
  const isInternalUser = useIsAuthUserAllowed(InternalRoles);

  const getAccordionHeader = ({ resales }: ResaleGroup) => {
    return (
      <ProtectedComponent
        requiredGroups={[Role.VisualArtistAdmin, Role.MemberServices, Role.Data, Role.ResaleReporter]}
        auth={auth}
      >
        <ResalesAccordionHeader resales={resales} isExternalUser={isExternalUser} resaleCount={resales.length} />
      </ProtectedComponent>
    );
  };

  const filter_by = [
    {
      field: "resale_status",
      in: [ResaleStatuses.PROVISIONALLY_ELIGIBLE, ResaleStatuses.PROVISIONALLY_INELIGIBLE],
    },
    {
      field: "batch_group_confirmed",
      eq: "false",
    },
  ];

  if (isInternalUser && vendorId) {
    filter_by.push({ field: "vendor_id", eq: vendorId.toString() });
  }

  return (
    <GroupedResales
      queryFunction={useListGroupedResalesQuery}
      getQueryVariables={(page) => ({
        input: { page, limit: 10, filter_by, sort_by },
      })}
      updateQueryVariablesWithFilter={({ input: { page, limit, filter_by, sort_by } }, new_filter_by) => {
        const finalFilterBy = filter_by.filter(
          (f) =>
            !(f.field === "resale_status" && new_filter_by && new_filter_by.some((nf) => nf.field === "resale_status")),
        );

        if (new_filter_by) {
          finalFilterBy.push(...new_filter_by);
        }

        return {
          input: {
            page,
            limit,
            filter_by: finalFilterBy,
            sort_by,
          },
        };
      }}
      getResaleGroupsFromQueryData={(data) => data.listGroupedResales.results}
      getPaginatedOutputFromQueryData={(data) => data.listGroupedResales}
      getAccordionHeader={getAccordionHeader}
      resaleStatusFilterOptions={resaleStatusFilterOptions}
      displayBanner={displayBanner}
    />
  );
};
